import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { getAdminFeedbackWebSocketUrl } from '../api/adminFeedback.api';
import { getAccessToken, refreshAccessToken } from '../../../../lib/axios';

const BASE_RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_DELAY_MS = 60000;

export const AdminFeedbackNotifications: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let disposed = false;
    let failedAttempts = 0;

    const scheduleReconnect = (forceRefresh: boolean) => {
      if (disposed) return;
      const delay = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** failedAttempts, MAX_RECONNECT_DELAY_MS);
      failedAttempts += 1;
      reconnectTimer = window.setTimeout(() => connect(forceRefresh), delay);
    };

    // Reuse the current access token. Refresh only when there is none or the previous
    // handshake was rejected: every refresh rotates the refresh-token cookie, and refreshing
    // on each reconnect raced other refreshes into token-reuse detection (all sessions revoked).
    const connect = async (forceRefresh = false) => {
      if (disposed) return;
      if (forceRefresh || !getAccessToken()) {
        try {
          await refreshAccessToken();
        } catch {
          scheduleReconnect(true);
          return;
        }
      }

      if (disposed) return;
      const url = getAdminFeedbackWebSocketUrl();
      if (!url) return;

      let opened = false;
      socket = new WebSocket(url);
      socket.onopen = () => {
        opened = true;
        failedAttempts = 0;
      };
      socket.onmessage = (event) => {
        if (event.data === 'pong') return;

        try {
          const payload = JSON.parse(event.data) as {
            type?: string;
            data?: { title?: string; userEmail?: string };
          };
          if (payload.type === 'feedback.created') {
            enqueueSnackbar(
              `New feedback: ${payload.data?.title || payload.data?.userEmail || ''}`,
              {
                variant: 'info',
              }
            );
          }
          window.dispatchEvent(new CustomEvent('admin-feedback:changed'));
        } catch {
          // Ignore malformed realtime payloads.
        }
      };
      socket.onclose = () => {
        // A socket that never opened was most likely rejected (e.g. expired token).
        scheduleReconnect(!opened);
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [enqueueSnackbar]);

  return null;
};
